import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { ProfileRow } from '@/types/database'
import { AuthContext } from './context'
import type { AuthValue } from './context'

async function fetchProfile(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
  if (error) throw error
  return data
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<ProfileRow | null>(null)
  const [loading, setLoading] = useState(true)
  const userId = session?.user.id ?? null

  useEffect(() => {
    let active = true

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setLoading(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setLoading(false)
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!userId) return
    // `active` guards against a stale fetch landing after a fast account
    // switch -- the later effect run wins.
    let active = true
    fetchProfile(userId)
      .then((row) => active && setProfile(row))
      .catch((e) => console.error('[auth] failed to load profile', e))
    return () => {
      active = false
    }
  }, [userId])

  /** Called from event handlers after the user edits their own profile. */
  const refreshProfile = useCallback(async () => {
    if (!userId) return
    setProfile(await fetchProfile(userId))
  }, [userId])

  // Presence: mark online while the tab is visible, offline on the way out.
  useEffect(() => {
    if (!userId) return

    const write = (presence: 'online' | 'away' | 'offline') =>
      supabase
        .from('profiles')
        .update({ presence, last_seen_at: new Date().toISOString() })
        .eq('id', userId)

    const onVisibility = () => void write(document.hidden ? 'away' : 'online')
    void write('online')
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      void write('offline')
    }
  }, [userId])

  const value = useMemo<AuthValue>(
    () => ({
      session,
      user: session?.user ?? null,
      // Derived rather than cleared in an effect: signing out must not leave
      // the previous account's profile on screen for even one frame.
      profile: userId && profile?.id === userId ? profile : null,
      loading,
      refreshProfile,
      signOut: async () => {
        await supabase.auth.signOut()
      },
    }),
    [session, profile, userId, loading, refreshProfile],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
